/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { MessageSquare } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useMe } from '../../lib/queries/me.js';
import { useThreads } from '../../lib/queries/messaging.js';

import { ChatPane } from './chat-pane.js';
import { NewMessageModal } from './new-message-modal.js';
import { ThreadList } from './thread-list.js';

/** Two-pane Messages (Figma): conversation list + chat. Shared by all portals. */
export function MessagesView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const threads = useThreads();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [newOpen, setNewOpen] = useState(false);

  const openNew = useCallback(() => {
    setNewOpen(true);
  }, []);
  const closeNew = useCallback(() => {
    setNewOpen(false);
  }, []);
  const onCreated = useCallback((threadId: string) => {
    setSelectedId(threadId);
    setNewOpen(false);
  }, []);

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      <QueryState
        isLoading={threads.isLoading}
        isError={threads.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('messages.empty')}
      >
        <ThreadList
          threads={threads.data ?? []}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onNew={openNew}
        />
        {selectedId !== undefined && me.data !== undefined ? (
          <ChatPane threadId={selectedId} meId={me.data.id} />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center text-slate-400">
            <MessageSquare className="mb-3 h-10 w-10" aria-hidden="true" />
            <p className="text-sm">{t('messages.pickConversation')}</p>
          </div>
        )}
      </QueryState>

      <Modal
        open={newOpen}
        onOpenChange={setNewOpen}
        title={t('messages.new')}
        description={t('messages.newSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <NewMessageModal onCreated={onCreated} onCancel={closeNew} />
      </Modal>
    </div>
  );
}
